/**
 * Canonical workspace tag keys for Bill-Lin Profile Magic.
 * All business fields use `business_*`; customers / คู่ค้าบ่อย use `customer_*`.
 */

export const PROFILE_MAGIC_TAG_KEYS = {
  businessTypeConfig: "business_type_config",
  businessBank: "business_bank",
  businessAccountName: "business_account_name",
  /** JSON string[] — account names from slip when Thai/English differ */
  businessAccountNames: "business_account_names",
  businessAccountNumber: "business_account_number",
  businessTaxId: "business_tax_id",
  businessAddress: "business_address",
  businessDisplayName: "business_display_name",
  businessLegalName: "business_legal_name",
  businessBranch: "business_branch",
  businessDocumentType: "business_document_type",
  businessOutstandingSamples: "business_outstanding_amount_samples",
  /** JSON [{ name, alias?, tax_id? }] — ลูกค้าประจำ + คู่ค้าบ่อย */
  customerAliases: "customer_aliases",
  businessLegacySheetRows: "business_legacy_sheet_rows",
} as const;

/** Older keys still loaded until workspace is re-saved */
export const PROFILE_MAGIC_TAG_LEGACY_ALIASES: Record<
  string,
  readonly string[]
> = {
  [PROFILE_MAGIC_TAG_KEYS.businessBank]: ["receiving_bank"],
  [PROFILE_MAGIC_TAG_KEYS.businessAccountName]: ["receiving_account_name"],
  [PROFILE_MAGIC_TAG_KEYS.businessAccountNames]: ["receiving_account_names"],
  [PROFILE_MAGIC_TAG_KEYS.businessAccountNumber]: ["receiving_account_number"],
  [PROFILE_MAGIC_TAG_KEYS.businessTaxId]: ["shop_tax_id"],
  [PROFILE_MAGIC_TAG_KEYS.businessAddress]: ["shop_address"],
  [PROFILE_MAGIC_TAG_KEYS.businessDisplayName]: ["shop_receipt_note"],
  [PROFILE_MAGIC_TAG_KEYS.businessLegalName]: ["shop_legal_name"],
  [PROFILE_MAGIC_TAG_KEYS.businessBranch]: ["shop_branch"],
  [PROFILE_MAGIC_TAG_KEYS.businessDocumentType]: ["shop_document_type"],
  [PROFILE_MAGIC_TAG_KEYS.businessOutstandingSamples]: [
    "outstanding_amount_samples",
  ],
  [PROFILE_MAGIC_TAG_KEYS.customerAliases]: ["regular_customer_aliases"],
  [PROFILE_MAGIC_TAG_KEYS.businessLegacySheetRows]: [
    "legacy_bank_statement_rows",
  ],
};

function lastTagValue(
  tags: Array<{ key: string; value: string }>,
  key: string,
): string {
  const matches = tags.filter(
    (t) => t.key.trim() === key && t.value?.trim(),
  );
  if (!matches.length) return "";
  return matches[matches.length - 1]!.value.trim();
}

/** Prefer canonical key; if duplicate rows share a key, use the last one (newest API row). */
export function readCanonicalTagValue(
  tags: Array<{ key: string; value: string }>,
  canonicalKey: string,
): string {
  const canonical = lastTagValue(tags, canonicalKey);
  if (canonical) return canonical;

  for (const legacyKey of PROFILE_MAGIC_TAG_LEGACY_ALIASES[canonicalKey] ??
    []) {
    const legacy = lastTagValue(tags, legacyKey);
    if (legacy) return legacy;
  }
  return "";
}

/** Tag map with legacy keys folded into canonical keys for readiness checks */
export function canonicalTagMap(
  tags: Array<{ key: string; value: string }>,
): Map<string, string> {
  const m = new Map<string, string>();
  for (const t of tags) {
    const k = t.key.trim();
    const v = t.value.trim();
    if (k && v) m.set(k, v);
  }
  for (const canonicalKey of Object.values(PROFILE_MAGIC_TAG_KEYS)) {
    if (m.get(canonicalKey)) continue;
    const resolved = readCanonicalTagValue(tags, canonicalKey);
    if (resolved) m.set(canonicalKey, resolved);
  }
  return m;
}
